import {LoadingSpinner, showToast} from 'common';
import {useHandleGlobalError} from 'error';
import {Pane} from 'evergreen-ui';
import {EditOfferForm, Offer, OfferView, useOfferByGuid} from 'offer';
import React, {useCallback} from 'react';
import {useHistory, useParams, useRouteMatch} from 'react-router';
import {PortalSecureRoute} from 'security';
import { AzurePortalSecuredRoute } from 'security/component/azure/AzurePortalSecuredRoute';
import {OfferReferralDetail} from './component/OfferReferralDetail';
import {StandardLayout} from './layouts/StandardLayout';
import { useApplicationAuthType } from 'common/component/ApplicationRootContext';
import {captureMessage} from 'monitoring';

export const ViewEditOfferPage = () => {
    const {offerGuid} = useParams<{ offerGuid: string; }>();
    const {loading, data, refetch} = useOfferByGuid(offerGuid);

    const {url: rootUrl} = useRouteMatch();
    const history = useHistory();
    // Leaving the edit route only swaps the child route, so this page stays mounted and its
    // offer query never runs again. Without the refetch the view shows what was loaded before
    // the edit. The query is no-cache, so the mutation result cannot reach it any other way.
    const successCallback = useCallback(async (newOffer: Partial<Offer>) => {
        showToast.success(`Offer for referral ${newOffer.referral?.caseNumber} was updated successfully`);
        // Leave the edit form even when the refetch fails, and keep the failure off screen.
        // The offer is already saved by this point, so anything that reads as an error invites
        // a second submit of the same offer. A failed refetch only leaves the view stale, which
        // is where it sat before the refetch existed.
        try {
            await refetch();
        } catch (e) {
            captureMessage(`Offer ${offerGuid} saved but the view refetch failed: ${(e as Error).message}`);
        } finally {
            history.push(rootUrl);
        }
    }, [rootUrl, history, refetch, offerGuid]);
    const cancelCallback = useCallback(() => {
        history.push(rootUrl);
    }, [rootUrl, history]);

    const handleError = useCallback((error: Error) => {
        showToast.error(error.message);
    }, [])
    useHandleGlobalError(handleError)
    const isInternal = useApplicationAuthType() === 'internal';

    return (
        <StandardLayout>
            {(loading) && <LoadingSpinner centerScreen/>}

            {!loading && data?.offer &&
            <Pane display='flex' className="portal-provider-content-container in-standard-layout">
                <OfferReferralDetail referral={data.offer.referral}/>

                <Pane className='flex-grow'>
                    {
                        isInternal ? (
                        <>
                            <AzurePortalSecuredRoute path={rootUrl} exact
                                               render={() => <OfferView offer={data.offer}/>}/>
                            <AzurePortalSecuredRoute path={`${rootUrl}/edit`}
                                               render={() => <EditOfferForm referral={data.offer.referral}
                                                                            existingOffer={data.offer}
                                                                            cancelCallback={cancelCallback}
                                                                            successCallback={successCallback}
                            />}/>
                        </>
                        ) : (
                        <>
                            <PortalSecureRoute path={rootUrl} exact
                                               render={() => <OfferView offer={data.offer}/>}/>
                            <PortalSecureRoute path={`${rootUrl}/edit`}
                                               render={() => <EditOfferForm referral={data.offer.referral}
                                                                            existingOffer={data.offer}
                                                                            cancelCallback={cancelCallback}
                                                                            successCallback={successCallback}
                            />}/>
                        </>

                        )
                    }
                </Pane>
            </Pane>
            }
        </StandardLayout>
    );
};
